import {
	Alert,
	Box,
	Button,
	Chip,
	CircularProgress,
	MenuItem,
	Paper,
	Select,
	Stack,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	TextField,
	Typography,
} from '@mui/material';
import { useCallback, useEffect, useState } from 'react';
import { FaCheck, FaPaperPlane, FaSearch } from 'react-icons/fa';
import { useNotify } from 'react-admin';
import constants from '../../constants';
import { httpClient } from '../../providers/myDataProvider';

// GH-679 — SOC2 privacy requests: someone emails Terra "delete my data" / "send me my data". Before
// acting she verifies it's really them — look the email up, send a one-time code to their phone
// (WhatsApp) or email, read the code here, ask them to repeat it, mark "verified". Every step is
// logged (the table below is the audit trail).

type Member = { _id: string; email: string; name: string; company: string | null; language: string; phone_masked: string | null; has_phone: boolean; active: boolean; created_at?: string; last_login?: string };
type Sent = { _id: string; code: string; channel: 'whatsapp' | 'email'; contact_masked: string; expires_at: string };
type Row = { _id: string; member_email: string; admin_email?: string; purpose: string; channel: string; contact_masked: string; code: string | null; expires_at: string; verified_at: string | null; verified_by: string | null; note: string | null; created_at: string };

const PURPOSE_HE: Record<string, string> = { delete: 'מחיקת מידע', data: 'קבלת מידע', other: 'אחר' };
const heDate = (iso?: string | null) => (iso ? new Date(iso).toLocaleString('he-IL', { dateStyle: 'short', timeStyle: 'short' }) : '—');

export default function IdentityVerification() {
	const notify = useNotify();
	const [email, setEmail] = useState('');
	const [searching, setSearching] = useState(false);
	const [results, setResults] = useState<Member[]>([]);
	const [member, setMember] = useState<Member | null>(null);
	const [purpose, setPurpose] = useState<'delete' | 'data' | 'other'>('delete');
	const [channel, setChannel] = useState<'whatsapp' | 'email'>('whatsapp');
	const [sending, setSending] = useState(false);
	const [sent, setSent] = useState<Sent | null>(null);
	const [note, setNote] = useState('');
	const [history, setHistory] = useState<Row[]>([]);

	const loadHistory = useCallback(async () => {
		try {
			const res = await httpClient(`${constants.API_URL}identity-verification/history?limit=50`, { method: 'GET' });
			setHistory(res.json as Row[]);
		} catch (e: any) {
			notify(e?.message || 'שגיאה בטעינת היסטוריה', { type: 'error' });
		}
	}, [notify]);

	useEffect(() => {
		loadHistory();
	}, [loadHistory]);

	const search = async () => {
		if (email.trim().length < 3) return;
		setSearching(true);
		setMember(null);
		setSent(null);
		try {
			// `q` = email or phone (server normalizes IL numbers, e.g. 052-… → +97252…)
			const res = await httpClient(`${constants.API_URL}identity-verification/lookup?q=${encodeURIComponent(email.trim())}`, { method: 'GET' });
			const rows = res.json as Member[];
			setResults(rows);
			if (rows.length === 1) pick(rows[0]);
			if (!rows.length) notify('לא נמצא משתמש עם המייל הזה', { type: 'warning' });
		} catch (e: any) {
			notify(e?.body?.message || e?.message || 'שגיאה בחיפוש', { type: 'error' });
		} finally {
			setSearching(false);
		}
	};

	const pick = (m: Member) => {
		setMember(m);
		setSent(null);
		setChannel(m.has_phone ? 'whatsapp' : 'email');
	};

	const send = async () => {
		if (!member) return;
		setSending(true);
		try {
			const res = await httpClient(`${constants.API_URL}identity-verification/send`, {
				method: 'POST',
				body: JSON.stringify({ memberId: member._id, purpose, channel }),
			});
			setSent(res.json as Sent);
			notify(channel === 'whatsapp' ? 'הקוד נשלח בוואטסאפ' : 'הקוד נשלח במייל', { type: 'success' });
			await loadHistory();
		} catch (e: any) {
			notify(e?.body?.message || e?.message || 'השליחה נכשלה', { type: 'error' });
		} finally {
			setSending(false);
		}
	};

	const verify = async () => {
		if (!sent) return;
		try {
			await httpClient(`${constants.API_URL}identity-verification/${sent._id}/verified`, { method: 'POST', body: JSON.stringify({ note }) });
			notify('סומן כמאומת', { type: 'success' });
			setSent(null);
			setNote('');
			await loadHistory();
		} catch (e: any) {
			notify(e?.body?.message || e?.message || 'שגיאה', { type: 'error' });
		}
	};

	return (
		<Box p={2} dir="rtl">
			<Typography variant="h5">אימות זהות לבקשות פרטיות</Typography>
			<Typography variant="body2" color="text.secondary" mb={2}>
				מישהו ביקש במייל למחוק את המידע שלו או לקבל אותו? מחפשים אותו כאן, שולחים לו קוד, מבקשים ממנו להקריא אותו — ומסמנים "אומת". הכל נרשם למטה.
			</Typography>

			<Paper sx={{ p: 2, mb: 2 }}>
				<Stack direction="row" spacing={1} alignItems="center">
					<TextField
						label="מייל או טלפון של המבקש/ת"
						size="small"
						value={email}
						onChange={(e) => setEmail(e.target.value)}
						onKeyDown={(e) => e.key === 'Enter' && search()}
						sx={{ minWidth: 340 }}
						inputProps={{ dir: 'ltr' }}
					/>
					<Button variant="contained" startIcon={<FaSearch />} onClick={search} disabled={searching}>
						{searching ? <CircularProgress size={18} /> : 'חיפוש'}
					</Button>
				</Stack>
				{results.length > 1 ? (
					<Stack direction="row" spacing={1} mt={1.5} flexWrap="wrap">
						{results.map((m) => (
							<Chip key={m._id} label={`${m.email} · ${m.name || '—'}${m.company ? ` · ${m.company}` : ''}`} onClick={() => pick(m)} color={member?._id === m._id ? 'primary' : 'default'} />
						))}
					</Stack>
				) : null}
			</Paper>

			{member ? (
				<Paper sx={{ p: 2, mb: 2 }}>
					<Stack direction="row" spacing={3} flexWrap="wrap" alignItems="center">
						<Box>
							<Typography variant="h6">{member.name || '—'}</Typography>
							<Typography variant="body2" dir="ltr" sx={{ textAlign: 'right' }}>{member.email}</Typography>
							<Typography variant="body2" color="text.secondary">
								{member.company || 'ללא חברה'} · {member.language === 'en' ? 'English' : 'עברית'} · {member.active ? 'פעיל' : 'לא פעיל'} · נרשם {heDate(member.created_at)} · כניסה אחרונה {heDate(member.last_login)}
							</Typography>
							<Typography variant="body2" color="text.secondary">
								טלפון: {member.phone_masked || 'אין'}
							</Typography>
						</Box>
						<Select size="small" value={purpose} onChange={(e) => setPurpose(e.target.value as any)}>
							<MenuItem value="delete">בקשת מחיקת מידע</MenuItem>
							<MenuItem value="data">בקשת קבלת מידע</MenuItem>
							<MenuItem value="other">אחר</MenuItem>
						</Select>
						<Select size="small" value={channel} onChange={(e) => setChannel(e.target.value as any)}>
							<MenuItem value="whatsapp" disabled={!member.has_phone}>וואטסאפ לטלפון</MenuItem>
							<MenuItem value="email">מייל</MenuItem>
						</Select>
						<Button variant="contained" startIcon={<FaPaperPlane />} onClick={send} disabled={sending || !!sent}>
							{sending ? <CircularProgress size={18} /> : 'שלח קוד'}
						</Button>
					</Stack>

					{sent ? (
						<Alert severity="info" sx={{ mt: 2 }} icon={false}>
							<Typography variant="body2">
								נשלח ב{sent.channel === 'whatsapp' ? 'וואטסאפ' : 'מייל'} ל-{sent.contact_masked} · תקף עד {heDate(sent.expires_at)}. בקש/י מהמבקש/ת להקריא את הקוד:
							</Typography>
							<Typography variant="h3" sx={{ letterSpacing: 8, fontFamily: 'monospace', my: 1 }} dir="ltr">
								{sent.code}
							</Typography>
							<Stack direction="row" spacing={1} alignItems="center">
								<TextField size="small" label="הערה (אופציונלי)" value={note} onChange={(e) => setNote(e.target.value)} sx={{ minWidth: 320 }} />
								<Button variant="contained" color="success" startIcon={<FaCheck />} onClick={verify}>
									הקוד תואם — אומת
								</Button>
								<Button variant="text" onClick={() => setSent(null)}>
									לא תואם / ביטול
								</Button>
							</Stack>
						</Alert>
					) : null}
				</Paper>
			) : null}

			<Typography variant="h6" mb={1}>
				היסטוריה (50 אחרונות)
			</Typography>
			<TableContainer component={Paper}>
				<Table size="small">
					<TableHead>
						<TableRow>
							<TableCell align="right">מתי</TableCell>
							<TableCell align="right">מבקש/ת</TableCell>
							<TableCell align="right">סוג בקשה</TableCell>
							<TableCell align="right">ערוץ</TableCell>
							<TableCell align="right">נשלח ל-</TableCell>
							<TableCell align="right">קוד</TableCell>
							<TableCell align="right">נשלח ע"י</TableCell>
							<TableCell align="right">אומת</TableCell>
							<TableCell align="right">הערה</TableCell>
						</TableRow>
					</TableHead>
					<TableBody>
						{history.map((r) => (
							<TableRow key={r._id} hover>
								<TableCell align="right">{heDate(r.created_at)}</TableCell>
								<TableCell align="right" dir="ltr" sx={{ textAlign: 'right' }}>{r.member_email}</TableCell>
								<TableCell align="right">{PURPOSE_HE[r.purpose] || r.purpose}</TableCell>
								<TableCell align="right">{r.channel === 'whatsapp' ? 'וואטסאפ' : 'מייל'}</TableCell>
								<TableCell align="right">{r.contact_masked}</TableCell>
								<TableCell align="right" dir="ltr" sx={{ textAlign: 'right', fontFamily: 'monospace' }}>{r.code || '••••••'}</TableCell>
								<TableCell align="right">{r.admin_email || '—'}</TableCell>
								<TableCell align="right">
									{r.verified_at ? <Chip size="small" color="success" label={`✓ ${heDate(r.verified_at)}${r.verified_by ? ` · ${r.verified_by}` : ''}`} /> : <Chip size="small" label="לא אומת" />}
								</TableCell>
								<TableCell align="right">{r.note || ''}</TableCell>
							</TableRow>
						))}
						{!history.length ? (
							<TableRow>
								<TableCell colSpan={9} align="right">
									<Typography variant="body2" color="text.secondary">עדיין לא נשלחו קודים.</Typography>
								</TableCell>
							</TableRow>
						) : null}
					</TableBody>
				</Table>
			</TableContainer>
		</Box>
	);
}
